import React from 'react';
import { Box, Container, Skeleton } from '@mui/material';

const RecommendationsSkeleton = () => {
  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* ═══ Header ═══ */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={38} height={38} animation="wave" sx={{ borderRadius: 2, flexShrink: 0 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton width={200} height={24} animation="wave" />
              <Skeleton variant="rounded" width={70} height={18} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>
            <Skeleton width={280} height={14} animation="wave" sx={{ mt: 0.3 }} />
          </Box>
        </Box>
      </Box>

      {/* ═══ Stats summary line ═══ */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Skeleton variant="circular" width={16} height={16} animation="wave" />
          <Skeleton width={70} height={16} animation="wave" />
          <Skeleton width={80} height={14} animation="wave" />
        </Box>
        <Box sx={{ width: 1, height: 14, bgcolor: '#e2e8f0' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Skeleton variant="circular" width={16} height={16} animation="wave" />
          <Skeleton width={100} height={16} animation="wave" />
          <Skeleton variant="rounded" width={28} height={16} animation="wave" sx={{ borderRadius: 0.75 }} />
        </Box>
      </Box>

      {/* ═══ Style tiles grid ═══ */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1, mb: 2 }}>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <Box key={i} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '10px 12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={30} height={30} animation="wave" sx={{ borderRadius: 1.5, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton width="75%" height={12} animation="wave" />
                <Skeleton width="50%" height={10} animation="wave" sx={{ mt: 0.3 }} />
              </Box>
              <Skeleton width={20} height={20} animation="wave" />
            </Box>
          </Box>
        ))}
      </Box>

      {/* ═══ High Conviction ═══ */}
      <Box sx={{ mb: 2 }}>
        {/* Toggle header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={8} height={8} animation="wave" />
            <Skeleton width={140} height={16} animation="wave" />
            <Skeleton variant="rounded" width={130} height={16} animation="wave" sx={{ borderRadius: 1 }} />
          </Box>
          <Skeleton width={18} height={18} animation="wave" />
        </Box>

        {/* Grid table */}
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
          {/* Header row */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: '32px 1.5fr 1.2fr 2.5fr 65px 55px',
            gap: '0 8px', p: '6px 12px',
            background: 'linear-gradient(180deg, #faf5ff 0%, #f5f3ff 100%)',
            borderBottom: '1px solid #e9d5ff',
          }}>
            {[15, 20, 70, 60, 40, 35].map((w, i) => (
              <Skeleton key={i} width={w} height={10} animation="wave" />
            ))}
          </Box>
          {/* Data rows */}
          {[0, 1, 2, 3, 4].map(i => (
            <Box key={i} sx={{
              display: 'grid', gridTemplateColumns: '32px 1.5fr 1.2fr 2.5fr 65px 55px',
              gap: '0 8px', p: '7px 12px', alignItems: 'center',
              borderBottom: i < 4 ? '1px solid #f8fafc' : 'none',
            }}>
              <Skeleton variant="rounded" width={22} height={22} animation="wave" sx={{ borderRadius: 1 }} />
              <Box>
                <Skeleton width={50} height={16} animation="wave" />
                <Skeleton width={80} height={10} animation="wave" sx={{ mt: 0.2 }} />
              </Box>
              <Box>
                <Skeleton width={70} height={12} animation="wave" />
                <Skeleton width={40} height={10} animation="wave" sx={{ mt: 0.2 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                {Array.from({ length: 2 + (i % 2) }).map((_, j) => (
                  <Skeleton key={j} variant="rounded" width={55} height={18} animation="wave" sx={{ borderRadius: 0.75 }} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                <Skeleton width={28} height={16} animation="wave" />
                <Skeleton variant="rounded" width={32} height={3} animation="wave" sx={{ borderRadius: 2 }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Skeleton variant="rounded" width={40} height={18} animation="wave" sx={{ borderRadius: 1 }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ═══ Main Table Card ═══ */}
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, overflow: 'hidden', bgcolor: '#fff' }}>
        {/* Style tabs */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {[85, 95, 90, 100, 80, 95, 85].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={32} animation="wave" sx={{ borderRadius: 1.5 }} />
          ))}
        </Box>
        {/* Table header */}
        <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1, borderBottom: '2px solid #e2e8f0' }}>
          {[30, 50, 80, 60, 50, 55, 60].map((w, i) => (
            <Skeleton key={i} width={w} height={12} animation="wave" />
          ))}
        </Box>
        {/* Table rows */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <Box key={i} sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            px: 2, py: 1,
            borderBottom: i < 7 ? '1px solid #f1f5f9' : 'none',
          }}>
            <Skeleton width={20} height={14} animation="wave" />
            <Skeleton width={48} height={16} animation="wave" />
            <Skeleton width={90} height={14} animation="wave" sx={{ flex: 1 }} />
            <Skeleton width={55} height={14} animation="wave" />
            <Skeleton width={45} height={14} animation="wave" />
            <Skeleton variant="rounded" width={50} height={20} animation="wave" sx={{ borderRadius: 1 }} />
            <Skeleton width={55} height={14} animation="wave" />
          </Box>
        ))}
      </Box>
    </Container>
  );
};

export default RecommendationsSkeleton;

